import React from "react"; // For JSX
import { Container } from "@/components/ui/container";

// Placeholder Icon Components
const IconFocus = () => (
  <svg
    className="h-8 w-8 text-primary"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
    />
  </svg>
);

const IconConnectivity = () => (
  <svg
    className="h-8 w-8 text-primary"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.368a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
    />
  </svg>
);

// Reusable Checkmark Icon
const IconCheck = () => (
  <svg
    className="h-5 w-5 text-green-500 mr-2 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="3"
      d="M5 13l4 4L19 7"
    />
  </svg>
);

// Skeleton Preview Components for WhyLocalLinksSection
const SkeletonPhoneAwayPreview = () => (
  <div className="mt-4 p-3 border border-dashed rounded-md bg-muted/50 flex flex-col items-center space-y-2">
    <div className="relative">
      <svg
        className="h-16 w-16 text-primary/30"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zm3 14a1 1 0 100-2 1 1 0 000 2z"
          clipRule="evenodd"
        />
      </svg>
      <svg
        className="h-8 w-8 text-red-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
        />
      </svg>
    </div>
    <p className="text-xs text-muted-foreground text-center">
      Enjoy your round, distraction-free.
    </p>
  </div>
);

const SkeletonAppFeaturesPreview = () => (
  <div className="mt-4 p-3 border border-dashed rounded-md bg-muted/50 space-y-2">
    <div className="p-1.5 rounded-md bg-background/70 border flex items-center space-x-2">
      <svg
        className="h-4 w-4 text-primary flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      </svg>
      <p className="text-xs text-muted-foreground">Profile & MMR Tracking</p>
    </div>
    <div className="p-1.5 rounded-md bg-background/70 border flex items-center space-x-2">
      <svg
        className="h-4 w-4 text-primary flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
      <p className="text-xs text-muted-foreground">League Chat & Feeds</p>
    </div>
    <div className="p-1.5 rounded-md bg-background/70 border flex items-center space-x-2">
      <svg
        className="h-4 w-4 text-primary flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <p className="text-xs text-muted-foreground">Club Dashboards & Tools</p>
    </div>
  </div>
);

export const WhyLocalLinksSection = () => {
  return (
    <section
      id="why-local-links"
      className="w-full py-12 md:py-24 lg:py-32 relative overflow-hidden"
      style={{
        backgroundImage: "url(/bgs/bg5.webp)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Dark dramatic overlay for the moody aesthetic */}
      <div className="absolute inset-0 bg-muted/90" />
      <Container className="relative z-10">
        <div className="text-center mb-12 md:mb-16 px-4 md:px-0">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Why Local Links?: Game On, Phone Off.
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto px-2 sm:px-0">
            Local Links is designed around a simple philosophy: your time on the
            course is for golf. We provide robust tools for everything that
            happens *around* your round— matchmaking, league management, club
            communication—so you can leave the digital distractions behind once
            you tee off.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-12">
          {/* Pillar 1: Maximize Your On-Course Focus */}
          <div className="flex flex-col p-4 sm:p-6 rounded-lg bg-background shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 pt-1">
                <IconFocus />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">
                  Maximize Your On-Course Focus
                </h3>
                <p className="text-muted-foreground mb-3">
                  Shift your digital interactions to before or after your game.
                  Local Links helps you prepare and connect, so your on-course
                  experience is pure, unadulterated golf.
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Pre-arrange matches, find partners, and confirm tee times
                      effortlessly.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Access league schedules, standings, and club news when
                      you're off the course.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Step onto the first tee with a clear mind, ready to enjoy
                      your game.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-auto pt-4">
              <SkeletonPhoneAwayPreview />
            </div>
          </div>

          {/* Pillar 2: Powerful Off-Course Connectivity */}
          <div className="flex flex-col p-4 sm:p-6 rounded-lg bg-background shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 pt-1">
                <IconConnectivity />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">
                  Powerful Off-Course Connectivity
                </h3>
                <p className="text-muted-foreground mb-3">
                  While we champion on-course presence, our app ensures you have
                  all the tools you need to manage your golf life effectively
                  between rounds.
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Robust communication: league/club channels, direct
                      messaging, activity feeds.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Track your MMR, view leaderboards, and analyze performance
                      post-round.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <IconCheck />
                    <span>
                      Stay connected with your local golf community without
                      on-course interruptions.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-auto pt-4">
              <SkeletonAppFeaturesPreview />
            </div>
          </div>
        </div>

        <div className="text-center mt-16 px-4 md:px-0">
          <p className="text-xl text-foreground max-w-2xl mx-auto px-2 sm:px-0">
            With Local Links, it's truly{" "}
            <span className="font-semibold text-primary">
              Game On, Phone Off
            </span>
            . Experience golf the way it was meant to be played.
          </p>
        </div>
      </Container>
    </section>
  );
};
